"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.measureDepth = exports.countLeaves = exports.crudFilterSchema = void 0;
const zod_1 = require("zod");
const filter_types_1 = require("./filter-types");
/**
 * Построить zod-схему AST-фильтра для CRUD list-запроса.
 *
 * Строится рекурсивно с **manual depth counter** (вместо z.lazy() — тот не даёт
 * depth-cap). На каждом уровне union: leaf | and-композиция | or-композиция |
 * not-композиция. На последнем разрешённом уровне (depth = maxDepth) — только leaf.
 *
 * Per-op value validation:
 * - `eq` / `neq` — string / number / boolean / null.
 * - `in` / `nin` — string[] | number[], max maxInArraySize элементов.
 * - `like` / `ilike` / `contains` / `starts_with` / `ends_with` — string max maxLikePatternLength.
 * - `gte` / `lte` / `gt` / `lt` — number | string (для datetime ISO).
 * - `is_null` / `is_not_null` — без value.
 * - `any_eq` — string / number; `any_in` — как `in`; `any_contains` — как `contains`.
 *   Поле у этих трёх — только из `arrayFields` (своё enum); без `arrayFields`
 *   leaf'ов для них нет вовсе, даже если оператор есть в `ops`.
 *
 * После parse'а — refine на leaves-count (max maxLeaves).
 *
 * @param fields — readonly tuple допустимых полей (`['name', 'status'] as const`).
 * @param ops — readonly tuple допустимых операторов (default — все CrudFilterOperators).
 * @param limits — переопределение default-лимитов (depth/leaves/sizes).
 * @param arrayFields — поля-массивы (подмножество `fields`) для `any_*`-операторов.
 *
 * @example
 *   const FooFilter = crudFilterSchema(
 *     ['name', 'status', 'createdAt'] as const,
 *     ['eq', 'in', 'like', 'gte'] as const,
 *   );
 *   const ast = FooFilter.parse({ and: [{ field: 'name', op: 'like', value: 'alice%' }] });
 */
const crudFilterSchema = (fields, ops, limits = {}, arrayFields = []) => {
    const lim = { ...filter_types_1.DEFAULT_CRUD_FILTER_LIMITS, ...limits };
    const strayArrayFields = arrayFields.filter((field) => !fields.includes(field));
    if (strayArrayFields.length > 0) {
        throw new Error(`crudFilterSchema: arrayFields [${strayArrayFields.join(', ')}] нет среди fields`);
    }
    // Операторы по элементам массива без полей-массивов — пустое обещание: не строим.
    const opsToUse = (ops ?? filter_types_1.CrudFilterOperators).filter((op) => !(0, filter_types_1.isCrudArrayFilterOp)(op) || arrayFields.length > 0);
    if (opsToUse.length === 0) {
        throw new Error('crudFilterSchema: из ops остались только any_*-операторы, а arrayFields пуст');
    }
    const fieldEnum = zod_1.z.enum(fields);
    const arrayFieldEnum = arrayFields.length > 0 ? zod_1.z.enum(arrayFields) : null;
    const stringValue = zod_1.z.string().max(lim.maxStringLength);
    const likeValue = zod_1.z.string().max(lim.maxLikePatternLength);
    const numberValue = zod_1.z.number().finite();
    /**
     * Schema для `value` per-operator. `is_null`/`is_not_null` возвращают
     * `z.undefined()` — value не должно быть передано.
     */
    const valueSchemaByOp = (op) => {
        switch (op) {
            case 'is_null':
            case 'is_not_null':
                return zod_1.z.undefined();
            case 'in':
            case 'nin':
            case 'any_in':
                return zod_1.z.union([
                    zod_1.z.array(stringValue).max(lim.maxInArraySize).min(1),
                    zod_1.z.array(numberValue).max(lim.maxInArraySize).min(1),
                ]);
            case 'like':
            case 'ilike':
            case 'contains':
            case 'starts_with':
            case 'ends_with':
            case 'any_contains':
                return likeValue;
            case 'any_eq':
                return zod_1.z.union([stringValue, numberValue]);
            case 'gte':
            case 'lte':
            case 'gt':
            case 'lt':
                return zod_1.z.union([numberValue, stringValue]);
            case 'eq':
            case 'neq':
                return zod_1.z.union([stringValue, numberValue, zod_1.z.boolean(), zod_1.z.null()]);
        }
    };
    /**
     * Leaf-schema: один z.object per operator (через literal на op),
     * затем union по всем операторам. .strict() — запрещает лишние ключи
     * (защита от случайных typo и attempt'ов obfuscation).
     */
    const leafSchemas = opsToUse.map((op) => {
        if (op === 'is_null' || op === 'is_not_null') {
            return zod_1.z
                .object({
                field: fieldEnum,
                op: zod_1.z.literal(op),
            })
                .strict();
        }
        return zod_1.z
            .object({
            // `any_*` — только по полям-массивам; opsToUse уже гарантирует, что они есть.
            field: (0, filter_types_1.isCrudArrayFilterOp)(op) && arrayFieldEnum ? arrayFieldEnum : fieldEnum,
            op: zod_1.z.literal(op),
            value: valueSchemaByOp(op),
        })
            .strict();
    });
    // z.union требует min 2 элемента tuple. TS не выводит tuple-shape из
    // dynamic-length массива leafSchemas — нужен cast через unknown. Runtime
    // гарантия: length >= 2 в этой ветке (length === 1 обработан выше).
    const leafSchema = leafSchemas.length === 1
        ? leafSchemas[0]
        : zod_1.z.union(leafSchemas);
    /**
     * Рекурсивный builder с depth-counter. На каждом уровне допустим:
     * leaf | {and: [...]} | {or: [...]} | {not: ...}.
     *
     * На depth >= maxDepth — допустим только leaf (запрещаем дальнейшую вложенность).
     * `and`/`or` массивы — min 1 (пустой массив бессмысленен).
     */
    const buildNodeSchema = (depth) => {
        if (depth >= lim.maxDepth) {
            return leafSchema;
        }
        const childSchema = buildNodeSchema(depth + 1);
        return zod_1.z.union([
            leafSchema,
            zod_1.z.object({ and: zod_1.z.array(childSchema).min(1) }).strict(),
            zod_1.z.object({ or: zod_1.z.array(childSchema).min(1) }).strict(),
            zod_1.z.object({ not: childSchema }).strict(),
        ]);
    };
    const rootSchema = buildNodeSchema(0);
    return rootSchema.superRefine((value, ctx) => {
        const leafCount = (0, exports.countLeaves)(value);
        if (leafCount > lim.maxLeaves) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: `Filter has too many leaf conditions: ${leafCount} (max ${lim.maxLeaves})`,
            });
        }
    });
};
exports.crudFilterSchema = crudFilterSchema;
/**
 * Рекурсивный walker — считает количество leaf-условий в AST.
 *
 * Leaf — узел с полем `field` (вне зависимости есть ли value).
 * Composition (and/or/not) — не считается leaf, считаются её под-узлы.
 *
 * Используется внутри `crudFilterSchema` для leaves-count cap, а также экспортируется
 * наружу для случая когда caller хочет проверить размер AST перед applyFilter.
 */
const countLeaves = (ast) => {
    if (ast.and)
        return ast.and.reduce((sum, n) => sum + (0, exports.countLeaves)(n), 0);
    if (ast.or)
        return ast.or.reduce((sum, n) => sum + (0, exports.countLeaves)(n), 0);
    if (ast.not)
        return (0, exports.countLeaves)(ast.not);
    if (ast.field)
        return 1;
    return 0;
};
exports.countLeaves = countLeaves;
/**
 * Рекурсивный walker — считает максимальную глубину AST.
 *
 * Корневой leaf = depth 0. Каждый `and`/`or`/`not` добавляет 1 к maximum глубины
 * под-деревьев.
 *
 * Утилитарный — для диагностики / логирования, не используется внутри schema
 * (depth-cap делается на этапе строительства schema через buildNodeSchema).
 */
const measureDepth = (ast) => {
    if (ast.and)
        return 1 + Math.max(0, ...ast.and.map(exports.measureDepth));
    if (ast.or)
        return 1 + Math.max(0, ...ast.or.map(exports.measureDepth));
    if (ast.not)
        return 1 + (0, exports.measureDepth)(ast.not);
    return 0;
};
exports.measureDepth = measureDepth;
//# sourceMappingURL=filter-zod.js.map